/**
 * PriorityQueue — editorial priority-action list shared by the BPA top-fixes
 * and the Secure Score recommendations sections. Each row has an italic
 * dropcap rank tinted by severity, the action title, a meta line, and an
 * optional ScoreDeltaChip showing the estimated Guardian Score lift.
 */

import type { ReactNode } from 'react'
import type { PriorityAction, Severity } from '@/lib/posture-brief'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'
import LearnMoreLink from '@/components/Dashboard/v2/LearnMoreLink'

const SEV_COLOR: Record<Severity, string> = {
  crit: '#C84B4B',
  high: '#C9AD80',
  med: '#4F86A8',
  low: '#4A9B6E',
  routine: '#9CA3AF',
}

interface PriorityQueueProps {
  actions: PriorityAction[]
  /** 1 = single column list, 2 = balanced two-column grid. Default: 2. */
  columns?: 1 | 2
  /** Rendered when actions[] is empty. */
  emptyState?: ReactNode
  onItemClick?: (action: PriorityAction) => void
}

export default function PriorityQueue({
  actions,
  columns = 2,
  emptyState,
  onItemClick,
}: PriorityQueueProps) {
  if (actions.length === 0) {
    return (
      <div
        className="
          rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]
          px-6 py-8 text-center text-[14px] italic
          text-[color:var(--color-mute,#6B7280)]
        "
      >
        {emptyState ?? 'Posture is settled. Nothing demands attention right now.'}
      </div>
    )
  }

  return (
    <ol
      className={
        columns === 2
          ? 'grid grid-cols-1 gap-x-10 gap-y-1 lg:grid-cols-2'
          : 'flex flex-col gap-1'
      }
    >
      {actions.map((action, idx) => (
        <PriorityRow
          key={action.id}
          action={action}
          rank={idx + 1}
          onClick={onItemClick}
        />
      ))}
    </ol>
  )
}

function PriorityRow({
  action,
  rank,
  onClick,
}: {
  action: PriorityAction
  rank: number
  onClick?: (a: PriorityAction) => void
}) {
  const sevColor = SEV_COLOR[action.severity] ?? SEV_COLOR.med
  const handleClick = () => onClick?.(action)
  const interactive = onClick != null

  return (
    <li
      className={[
        'group relative flex items-start gap-5 border-b py-4',
        'border-[color:var(--color-line,#E6E8EC)]',
        interactive
          ? 'cursor-pointer transition-[padding] duration-150 hover:pl-1.5'
          : '',
      ].join(' ')}
      onClick={interactive ? handleClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClick()
              }
            }
          : undefined
      }
    >
      <span
        aria-hidden
        className="
          font-display flex-none select-none italic font-extrabold
          tabular-nums leading-none tracking-[-0.02em]
        "
        style={{
          fontSize: '34px',
          color: sevColor,
          minWidth: '38px',
        }}
      >
        {String(rank).padStart(2, '0')}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div
            className="text-[15px] font-semibold leading-snug"
            style={{ color: 'rgb(var(--color-text-primary,33 41 56))' }}
          >
            {action.title}
          </div>
          {/* Editorial palette — sits on cream/paper, so the muted token reads
              correctly here instead of the dark-surface defaults. */}
          <LearnMoreLink
            url={action.learnMoreUrl}
            size="sm"
            className="inline-flex flex-none items-center justify-center rounded text-[color:var(--color-faint,#9CA3AF)] transition-colors hover:text-[color:var(--color-navy,#212938)]"
          />
        </div>

        {(action.meta || typeof action.scope === 'number' || typeof action.frameworksAffected === 'number') && (
          <div
            className="
              mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5
              text-[11px] uppercase tracking-[0.12em]
              text-[color:var(--color-mute,#6B7280)]
            "
          >
            {action.meta && <span>{action.meta}</span>}
            {typeof action.scope === 'number' && action.scope > 0 && (
              <span aria-label={`${action.scope} affected`}>· {action.scope} scope</span>
            )}
            {typeof action.frameworksAffected === 'number' && action.frameworksAffected > 0 && (
              <span>· {action.frameworksAffected} framework{action.frameworksAffected !== 1 ? 's' : ''}</span>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-none items-center gap-2 pt-1">
        <ScoreDeltaChip delta={action.guardianScoreDelta} />
        {interactive && (
          <span
            aria-hidden
            className="text-[16px] text-[color:var(--color-faint,#9CA3AF)] group-hover:text-[color:var(--color-mute,#6B7280)]"
          >
            →
          </span>
        )}
      </div>
    </li>
  )
}
